import { formatDate } from "@angular/common";
import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { AnnualStatementsService } from "@shared/services/annual-statements.service";
import { CoverLettersApiService } from "@shared/services/cover-letters-api.service";
import { DocumentsApiService } from "@shared/services/documents-api.service";
import { DocumentsService } from "@shared/services/documents.service";
import { Guid } from "@shared/utils/guid.helper";
import { NgxSkeletonLoaderModule } from "ngx-skeleton-loader";
import { catchError, filter, forkJoin, Observable, of, Subject, tap } from "rxjs";
import { DocumentationType } from '@models/documents/document-enums';
import { AnnualChangeData } from "@models/annual-statements/annualChangeData";
import { AnnualStatement } from "@models/annual-statements/annual-statement";
import { Company } from "@models/companies/company";
import { CoverLetter } from "@models/documents/cover-letter";
import { DocumentSelection } from "@models/documents/document-selection";
import { SelectOption } from "@models/ui-models/select-options";
import { SelectComponent } from "@shared/controls/select/select.component";
import { DocumentSelectionComponent } from "@shared/components/documents-related/document-selection/document-selection.component";

@Component({
  selector: 'app-annual-generate-documents',
  standalone: true,
  templateUrl: './annual-generate-documents.component.html',
  imports: [
    SelectComponent,
    FormsModule,
    DocumentSelectionComponent,
    NgxSkeletonLoaderModule
  ],
  providers: [CoverLettersApiService],
  styleUrl: './annual-generate-documents.component.scss'
})
export class AnnualGenerateDocumentsComponent implements OnInit {
  annualStatementsService = inject(AnnualStatementsService);
  documentsService = inject(DocumentsService);
  documentsApiService = inject(DocumentsApiService);
  coverLetterService = inject(CoverLettersApiService);
  destroyRef = inject(DestroyRef);

  @Input() annual: AnnualStatement | undefined;
  @Input() company!: Company;
  @Input() confirmAction$: Subject<boolean> | undefined;
  @Input() isSubmitting = false;

  @Output() generateAnnualStatementE = new EventEmitter<AnnualStatement>();

  readonly DocumentationType = DocumentationType;
  readonly solvencyTemplateOptions: SelectOption[] = [
    { label: 'Solvency Resolution', value: '1' },
    { label: 'Solvency Minutes', value: '2' }
  ];

  coverLetterTemplateOptions: SelectOption[] = [{ label: 'Default Cover Letter', value: '-1' }];
  selectedCoverLetterId = '';
  solvencyTemplate = '';

  documentSelection: DocumentSelection[] | undefined;
  coverLetters: CoverLetter[] = [];
  documentationType: DocumentationType | undefined;
  loading = false;

  ngOnInit(): void {
    this.annualStatementsService.disabledHeaderBtn.set(true);
    this.loadData();
    this.listenHeaderButton();
    this.documentationType = this.annual?.changeAuthorisation?.documentationType;
  }

  onCoverLetterSelect(coverLetter: string): void {
    this.selectedCoverLetterId = coverLetter;
    this.annualStatementsService.disabledHeaderBtn.set(!this.isValidForm());
  }

  loadData(): void {
    this.loading = true;

    const documentsSelection$: Observable<DocumentSelection[]> = this.documentsApiService
      .getDocumentsSelection(this.annual?.documentId ?? '').pipe(
        catchError((err) => {
            console.error(err);
            return of([]);
          }
        )
      );

    const coverLetters$: Observable<CoverLetter[]> = this.coverLetterService.loadCoverLetters().pipe(
      catchError((err) => {
        console.error(err);
        return of([]);
      })
    );

    forkJoin([documentsSelection$, coverLetters$]).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(([documentsSelection, coverLetters]) => {
      const selection = this.annual?.changeAuthorisation?.documentSelection?.selection ?? [];
      documentsSelection.forEach(document => {
        const matched = selection.find(item => item.fileId === document.fileId || item.fileName === document.fileName);
        document.included = matched?.included ?? document.included;
        document.sequence = matched?.sequence ?? document.sequence;
      });
      this.documentSelection = [...documentsSelection];
      this.coverLetters = [...coverLetters];
      this.coverLetterTemplateOptions = this.coverLetterTemplateOptions.concat(...(coverLetters ?? []).map(coverLetter => ({
        label: coverLetter.title,
        value: coverLetter.id
      })));

      this.selectedCoverLetterId = (!this.annual?.changes[0]?.coverLetterId || this.annual?.changes[0].coverLetterId == Guid.EmptyGuid)
        ? '-1'
        : this.annual?.changes[0]?.coverLetterId;
      this.annualStatementsService.disabledHeaderBtn.set(!this.isValidForm());
      this.loading = false;
    });
  }

  onDocumentSelectionChange(documentSelections: DocumentSelection[]): void {
    this.documentSelection = documentSelections;
    this.annualStatementsService.disabledHeaderBtn.set(!this.isValidForm());
  }

  isValidForm(): boolean {
    return !!((this.selectedCoverLetterId) && this.documentSelection?.some(d => d.included));
  }

  listenHeaderButton(): void {
    this.confirmAction$
      ?.pipe(
        filter(confirm => confirm),
        tap(() => this.generateAnnualStatement()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (res) => {
          this.confirmAction$?.next(false);
        },
        error: (error) => {
          console.warn('[AnnualStatementGenerateDocuments.confirmAction$] error: ', error);
        }
      });
  }

  generateAnnualStatement(): void {
    const annual = this.annual;
    const coverLetter = this.coverLetters.find(letter => letter.id === this.selectedCoverLetterId);
    if (annual) {
      annual.changes[0] ??= new AnnualChangeData();
      if (coverLetter) {
        annual.changes[0].coverLetterId = coverLetter.id;
        annual.changes[0].coverLetterName = coverLetter.title;
        annual.changes[0].coverFileName = coverLetter.fileName;
        annual.changes[0].isDefaultCoverLetter = false;
      } else {
        annual.changes[0].coverLetterId = null;
        annual.changes[0].coverLetterName = '';
        annual.changes[0].coverFileName = '';
        annual.changes[0].isDefaultCoverLetter = true;
      }
      annual.changes[0].changeDate = formatDate(new Date(), 'yyyy-MM-dd', 'en-US') as unknown as Date;
      annual.changes[0].reviewDate = annual.reviewDate ?? annual.changes[0].reviewDate;

      if (annual.changeAuthorisation && this.documentSelection?.length) {
        annual.changeAuthorisation.documentSelection = { selection: this.documentSelection };
      }

      if (annual.changeAuthorisation?.meetingDate) {
        annual.changeAuthorisation.meetingDate = formatDate(annual.changeAuthorisation?.meetingDate ?? '', 'yyyy-MM-dd', 'en-US') as unknown as Date;
      }

      annual.documentSigning = null;
    }

    if (annual) {
      this.generateAnnualStatementE.emit(annual);
    }
  }
}
